import getConfig from "./config";
import {ajax, getResponseData} from "./ajax";
import {
    convertToArray,
    convertToPlainObject,
    convertToString,
    getMoment,
    isObject,
    isRegularNumber,
    parseOptions,
    validateDateInput,
} from "./helpers";
import {getFancybox, showFancybox} from "./fancybox";
import {setupDatepicker} from "./datepicker";

const CONFIG_DEFAULTS = {
    showEventDetailsInModal: false,
    showEventsSidebar: false,
};
let {
    showEventDetailsInModal: SHOW_EVENT_DETAILS_IN_MODAL,
    showEventsSidebar: SHOW_EVENTS_SIDEBAR,
} = CONFIG_DEFAULTS;
const EVENT_DEFAULT_FIELDS = ["title", "event_date"];
const EVENT_TYPE_DEFAULT = "event";
const EVENTS_LAYOUT_CALENDAR = "calendar";
const EVENTS_LAYOUT_LIST = "list";
const EVENTS_VIEW_HTML = "html";
const EVENTS_VIEW_RAW = "raw";

const config = getConfig();
let currentLayout = EVENTS_LAYOUT_CALENDAR;
let currentLayoutToggle = EVENTS_LAYOUT_LIST;
let dateChangedYear = null;
let dateChangedMonth = null;
const datesFound = {};
let eventTypes = [EVENT_TYPE_DEFAULT];
let terms = {};
let fancybox;

const clickHandler = (e) => {
    if (!SHOW_EVENT_DETAILS_IN_MODAL) {
        return;
    }

    const {link, calendar, cell} = {
        link: e.target.closest("a"),
        calendar: e.target.closest(".calendar"),
        cell: e.target.closest("td, th"),
    };

    if (!link || !calendar || !cell || !cell.classList.contains("has-event")) {
        return;
    }

    e.preventDefault();
    window.dispatchEvent(
        new CustomEvent(`${config.appName}/eventClick`, {
            detail: {link, calendar, cell},
        }),
    );
};

const eventClickHandler = async (e) => {
    let {parsedOptions, invalidOptions} = parseOptions(e.detail, {
        calendar: null,
        cell: null,
        link: null,
    });
    invalidOptions = Object.keys(invalidOptions);

    if (0 < invalidOptions.length) {
        console.error(
            `Invalid (empty) options passed to callback showModal: ${invalidOptions.join(
                ", ",
            )}`,
        );
        return;
    }

    const {calendar, cell, link} = parsedOptions;

    let month = Number(cell.getAttribute("data-month"));
    month = Number.isNaN(month) ? month : month + 1;
    const year = Number(cell.getAttribute("data-year"));
    const day = Number(link.getAttribute("data-date"));

    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
        return;
    }

    const yearDisplay = year.toString();
    const monthDisplay = month.toString().padStart(2, "0");
    const dayDisplay = day.toString().padStart(2, "0");
    const dateStr = `${yearDisplay}-${monthDisplay}-${dayDisplay}`;
    const dateStrWithTerms = `${dateStr}-${JSON.stringify(terms)}`;
    let eventsIdPrefix = calendar.getAttribute("id");
    eventsIdPrefix = eventsIdPrefix ? `${eventsIdPrefix}-` : "";
    const eventsId = `${eventsIdPrefix}events-${dateStrWithTerms}`.replace(
        /[^A-Za-z0-9_-]/g,
        "",
    );

    let eventsEl = calendar.querySelector(`#${eventsId}`);

    if (!eventsEl) {
        eventsEl = document.createElement("div");
        eventsEl.style.display = "none";
        eventsEl.setAttribute("id", eventsId);
        eventsEl.classList.add("calendar__date-events");
        calendar.appendChild(eventsEl);
    }

    await showModal({
        calendar,
        dateStr,
        dateStrWithTerms,
        eventsEl,
        eventsId,
    });
};

const getEvents = async (
    dateStart,
    dateEnd = null,
    view = EVENTS_VIEW_RAW,
    fields = EVENT_DEFAULT_FIELDS,
    eventTypes = [EVENT_TYPE_DEFAULT],
    terms = {},
) => {
    view = "string" === typeof view ? view : EVENTS_VIEW_RAW;
    fields = Array.isArray(fields)
        ? fields
        : "string" === typeof fields
          ? [fields]
          : [];
    eventTypes = convertToArray(eventTypes);
    eventTypes = 0 < eventTypes.length ? eventTypes : [EVENT_TYPE_DEFAULT];
    terms = convertToPlainObject(terms);

    const moment = await getMoment();
    const dateStartValidated = await validateDateInput(dateStart);

    if (!dateStartValidated) {
        console.error(
            "Supplied dateStart parameter to function getEvents() must be an instance of moment(), a string, an object, a finite (non-BigInt) number, or undefined",
            dateStart,
        );
        return null;
    }

    dateStart = dateStart instanceof moment ? dateStart : moment(dateStart);
    if (Number.isNaN(dateStart.unix())) {
        console.error(
            "Supplied dateStart parameter to function getEvents() is an invalid date",
            dateStart,
        );
        return null;
    }

    const dateEndValidated = await validateDateInput(dateEnd);
    if (dateEndValidated) {
        dateEnd = dateEnd instanceof moment ? dateEnd : moment(dateEnd);
        if (Number.isNaN(dateEnd.unix())) {
            console.error(
                "Supplied dateEnd parameter to function getEvents() is an invalid date",
            );
            return null;
        }
    }

    const {yearStart, monthStart, dayStart} = {
        yearStart: dateStart.year(),
        monthStart: dateStart.month() + 1,
        dayStart: dateStart.date(),
    };
    const {yearStartStr, monthStartStr, dayStartStr} = {
        yearStartStr: yearStart.toString(),
        monthStartStr: monthStart.toString().padStart(2, "0"),
        dayStartStr: dayStart.toString().padStart(2, "0"),
    };
    const dateStartStr = `${yearStartStr}-${monthStartStr}-${dayStartStr}`;
    const {yearEnd, monthEnd, dayEnd} = {
        yearEnd: dateEnd?.year() ?? yearStart,
        monthEnd: (dateEnd?.month() ?? monthStartStr - 1) + 1,
        dayEnd: dateEnd?.date() ?? dayStart,
    };
    const {yearEndStr, monthEndStr, dayEndStr} = {
        yearEndStr: yearEnd.toString(),
        monthEndStr: monthEnd.toString().padStart(2, "0"),
        dayEndStr: dayEnd.toString().padStart(2, "0"),
    };
    const dateEndStr = `${yearEndStr}-${monthEndStr}-${dayEndStr}`;

    const response = await ajax({
        action: config.appName + "/get_events",
        event_types: eventTypes,
        date_start: dateStartStr,
        date_end: dateEndStr,
        fields,
        view,
        terms,
    });

    const responseData = await getResponseData(response);
    if (!validateEventQueryResponseData(responseData, view)) {
        throw new Error("Unable to retrieve events");
    }

    return responseData?.data?.events;
};

const getMonthEvents = async (
    year = null,
    month = null,
    eventTypes = [EVENT_TYPE_DEFAULT],
    terms = {},
) => {
    eventTypes = convertToArray(eventTypes);
    eventTypes = 0 < eventTypes.length ? eventTypes : [EVENT_TYPE_DEFAULT];
    terms = convertToPlainObject(terms);

    let momentInput = undefined;
    const moment = await getMoment();

    year = isRegularNumber(year) ? year : 0;
    month = isRegularNumber(month) ? month : 0;
    if (0 < year && 0 < month) {
        momentInput = {year, month: month - 1};
    }

    const date = moment(momentInput).endOf("month");
    const day = date.date().toString().padStart(2, "0");
    month = (date.month() + 1).toString().padStart(2, "0");
    year = date.year().toString();

    const response = await ajax({
        action: config.appName + "/get_events",
        event_types: eventTypes,
        date_start: `${year}-${month}-01`,
        date_end: `${year}-${month}-${day}`,
        fields: [
            "title",
            "content",
            "event_date",
            "event_link",
            "agenda",
            "minutes",
            "video",
        ],
        terms,
    });

    const responseData = await getResponseData(response);
    if (!validateEventQueryResponseData(responseData)) {
        throw new Error("Unable to retrieve events");
    }

    return responseData?.data?.events;
};

const setupCalendar = async (calendar) => {
    const layout = calendar.getAttribute("data-layout");

    if (layout) {
        currentLayout = layout;

        if (EVENTS_LAYOUT_LIST === currentLayout) {
            currentLayoutToggle = EVENTS_LAYOUT_CALENDAR;
        }
    } else {
        calendar.setAttribute("data-layout", currentLayout);
    }

    let calendarConfig = CONFIG_DEFAULTS;

    try {
        calendarConfig = JSON.parse(calendar.getAttribute("data-config"));
    } catch (e) {
        console.error(e);
    }

    ({
        showEventDetailsInModal: SHOW_EVENT_DETAILS_IN_MODAL,
        showEventsSidebar: SHOW_EVENTS_SIDEBAR,
    } = convertToPlainObject(calendarConfig, CONFIG_DEFAULTS));
    const eventsLabelTemplate = calendar.getAttribute(
        "data-events-label-template",
    );
    const eventsLabelEl = calendar.querySelector(".calendar__events-title");
    const eventTypesRaw =
        calendar.getAttribute("data-post-types") ||
        JSON.stringify([EVENT_TYPE_DEFAULT]);
    const eventTypesBackup = eventTypes;
    eventTypes = convertToArray(JSON.parse(eventTypesRaw));
    eventTypes = 0 >= eventTypes.length ? eventTypesBackup : eventTypes;

    const filterMonthYear = calendar.querySelector(
        ".calendar__filter-month-year",
    );
    const filterMonthYearMonth = calendar.querySelector(
        ".calendar__filter-month-year-month select",
    );
    const filterMonthYearYear = calendar.querySelector(
        ".calendar__filter-month-year-year select",
    );
    const filterMonthYearButton = filterMonthYear?.querySelector(
        ".calendar__filter-month-year-submit button",
    );
    filterMonthYearButton?.addEventListener("click", async () => {
        let {selectedMonth, selectedYear} = {
            selectedMonth: Number(filterMonthYearMonth?.value ?? NaN),
            selectedYear: Number(filterMonthYearYear?.value ?? NaN),
        };

        if (!Number.isNaN(selectedMonth) && !Number.isNaN(selectedYear)) {
            selectedMonth = ("" + selectedMonth).padStart(2, "0");
            window
                .jQuery(datepicker)
                .datepicker("setDate", `${selectedMonth}/01/${selectedYear}`);
        }
    });

    const layoutButton = calendar.querySelector(
        ".calendar__button-layout .button",
    );
    if (layoutButton) {
        let layoutButtonOptions = null;

        try {
            layoutButtonOptions = JSON.parse(
                layoutButton.getAttribute("data-options"),
            );
        } catch (e) {
            console.error(e);
        }

        if (layoutButtonOptions) {
            layoutButton.addEventListener("click", (e) => {
                e.preventDefault();
                updateLayoutButton(layoutButton, layoutButtonOptions, calendar);
            });
        }
    }

    for (const select of [
        ...calendar.querySelectorAll(".calendar__filter-taxonomy select"),
    ]) {
        const taxonomy = select.getAttribute("data-taxonomy");

        if (!taxonomy) {
            continue;
        }

        select.addEventListener("change", async () => {
            if (!select.value) {
                return;
            }

            try {
                const termIds = convertToArray(JSON.parse(select.value));
                terms = {
                    [taxonomy]: termIds,
                };
                await updateCalendar(
                    datepicker,
                    calendar,
                    dateChangedYear,
                    dateChangedMonth,
                    eventsLabelEl,
                    eventsLabelTemplate,
                );
            } catch (error) {
                console.error(error);
            }
        });
    }

    const datepicker = calendar.querySelector(".datepicker");
    if (datepicker) {
        datepicker.addEventListener(
            config.appName + "/datepickerUpdated",
            async (e) => {
                const table = calendar.querySelector(
                    "table.ui-datepicker-calendar:not(.calendar__datepicker-wrap > *)",
                );
                if (table) {
                    const tableWrap = document.createElement("div");
                    tableWrap.classList.add("calendar__datepicker-wrap");
                    table.parentElement.insertBefore(tableWrap, table);
                    tableWrap.appendChild(table);
                }

                const datepicker = e.detail?.datepicker;

                if (datepicker) {
                    const calendar = datepicker.closest(".calendar");
                    await updateCalendar(
                        datepicker,
                        calendar,
                        dateChangedYear,
                        dateChangedMonth,
                        eventsLabelEl,
                        eventsLabelTemplate,
                    );
                }
            },
        );
        datepicker.addEventListener(
            config.appName + "/datepickerChangeMonthYear",
            (e) => {
                const date = convertToPlainObject(e.detail ?? {}, {
                    year: 0,
                    month: 0,
                });
                const {year, month} = date;
                dateChangedYear = isRegularNumber(year)
                    ? year
                    : Number.parseInt(convertToString(year), 10);
                dateChangedYear = Number.isNaN(dateChangedYear) ? 0 : year;
                dateChangedMonth = isRegularNumber(month)
                    ? month
                    : Number.parseInt(convertToString(month), 10);
                dateChangedMonth = Number.isNaN(dateChangedMonth) ? 0 : month;
            },
        );
        await setupDatepicker(datepicker);
    }
};

const setupEvents = async () => {
    window.addEventListener("click", clickHandler);
    window.addEventListener(`${config.appName}/eventClick`, eventClickHandler);

    const calendars = [...document.querySelectorAll(".calendar")];
    for (const calendar of calendars) {
        await setupCalendar(calendar);
    }
};

const showModal = async (options) => {
    let {parsedOptions, invalidOptions} = parseOptions(
        options,
        {
            calendar: null,
            dateStr: "",
            dateStrWithTerms: "",
            eventsEl: null,
            eventsId: "",
        },
        ["calendar", "dateStr", "dateStrWithTerms", "eventsEl", "eventsId"],
    );
    invalidOptions = Object.keys(invalidOptions);

    if (0 < invalidOptions.length) {
        console.error(
            `Invalid (empty) options passed to function showModal: ${invalidOptions.join(
                ", ",
            )}`,
        );
        return;
    }

    const {calendar, dateStr, dateStrWithTerms, eventsEl, eventsId} =
        parsedOptions;
    invalidOptions = {};

    if (!(calendar instanceof Element)) {
        invalidOptions.calendar = {
            message:
                "The parameter 'calendar' for function showModal must be an instance of Element",
            input: calendar,
        };
    }

    if (!(eventsEl instanceof Element)) {
        invalidOptions.eventsEl = {
            message:
                "The parameter 'eventsEl' for function showModal must be an instance of Element",
            input: eventsEl,
        };
    }

    if ("undefined" === typeof datesFound[dateStrWithTerms]) {
        //calendar.classList.add("loading");
        const html = await getEvents(
            dateStr,
            dateStr,
            EVENTS_VIEW_HTML,
            EVENT_DEFAULT_FIELDS,
            eventTypes,
            terms,
        );

        const eventsElNew = eventsEl.cloneNode(true);
        eventsElNew.innerHTML = html;
        eventsElNew
            .querySelectorAll("[id]")
            .forEach((el) =>
                el.setAttribute("id", `${eventsId}-${el.getAttribute("id")}`),
            );
        datesFound[dateStrWithTerms] = eventsEl.innerHTML =
            eventsElNew.innerHTML;
    }

    window.addEventListener(`${config.appName}/fancyboxReady`, () => {
        calendar.classList.remove("loading");
    });

    await showFancybox(
        [
            {
                src: `#${eventsId}`,
                type: "inline",
            },
        ],
        config.fancyboxOptions.inlineHtml,
    );
};

const updateCalendar = async (
    datepicker,
    calendar,
    year = null,
    month = null,
    eventsLabelEl = null,
    eventsLabelTemplate = "",
    extraLoadingClass = "",
) => {
    if (!datepicker || !calendar) {
        !datepicker &&
            console.error(
                "Parameter 'datepicker' for callback updateCalendar is required",
                datepicker,
            );
        !calendar &&
            console.error(
                "Parameter 'calendar' for callback updateCalendar is required",
                calendar,
            );
        return;
    }

    try {
        calendar.setAttribute("data-no-events", "false");

        const moment = await getMoment();

        if (eventsLabelEl && eventsLabelTemplate && year && month) {
            const yearParsed = Number(year);
            const monthParsed = Number(month);

            if (!Number.isNaN(yearParsed) && !Number.isNaN(monthParsed)) {
                const monthDisplay = moment(
                    `${yearParsed.toString()}-${monthParsed
                        .toString()
                        .padStart(2, "0")}-01`,
                ).format("MMMM");
                eventsLabelEl.innerHTML = eventsLabelTemplate.replace(
                    "%month%",
                    monthDisplay,
                );
            }
        }

        let eventsIdPrefix = calendar.getAttribute("id");
        eventsIdPrefix = eventsIdPrefix ? `${eventsIdPrefix}-` : "";
        let eventsTable;
        const noEventsMessage = calendar.querySelector(".calendar__no-events");

        if (SHOW_EVENTS_SIDEBAR) {
            eventsTable = calendar.querySelector(".calendar__events tbody");
            eventsTable.innerHTML = "";
            noEventsMessage?.classList?.add("d-none");
        }

        calendar.classList.add(
            ...["loading", extraLoadingClass].filter(Boolean),
        );
        [...datepicker.querySelectorAll("td")].forEach((td) => {
            window.jQuery(td).off("click");
            td.classList.add("ui-datepicker-unselectable");
            td.classList.remove("has-event");

            if (!SHOW_EVENT_DETAILS_IN_MODAL) {
                for (const eventsList of td.querySelectorAll(
                    ".calendar__day-events",
                )) {
                    eventsList.remove();
                }
            }

            let month = Number(td.getAttribute("data-month"));
            month = Number.isNaN(month) ? month : month + 1;
            const year = Number(td.getAttribute("data-year"));
            const link = td.querySelector("a.ui-state-default");
            link.setAttribute("tabindex", "-1");
            link.setAttribute("role", "presentation");
            const day = Number(link.getAttribute("data-date"));

            if (
                Number.isNaN(year) ||
                Number.isNaN(month) ||
                Number.isNaN(day)
            ) {
                return;
            }

            const yearDisplay = year.toString();
            const monthDisplay = month.toString().padStart(2, "0");
            const dayDisplay = day.toString().padStart(2, "0");
            const dateStr = `${yearDisplay}-${monthDisplay}-${dayDisplay}`;
            const dateStrWithTerms = `${dateStr}-${JSON.stringify(terms)}`;
            const dateStrDisplay = `${monthDisplay}/${dayDisplay}/${yearDisplay}`;
            const eventsId =
                `${eventsIdPrefix}events-${dateStrWithTerms}`.replace(
                    /[^A-Za-z0-9_-]/g,
                    "",
                );

            link.normalize();
            const textNode = link.firstChild;

            if (
                textNode.nodeType === Node.TEXT_NODE &&
                0 < textNode.textContent.trim().length
            ) {
                const span = document.createElement("span");
                span.classList.add("calendar__day");
                span.setAttribute("aria-hidden", "true");
                textNode.parentNode.insertBefore(span, textNode);
                span.appendChild(textNode);
            }

            const mobileDate = document.createElement("span");
            mobileDate.classList.add("calendar__day-date");
            mobileDate.textContent = dateStrDisplay;
            link.appendChild(mobileDate);

            let eventsEl = calendar.querySelector(`#${eventsId}`);

            if (!eventsEl) {
                eventsEl = document.createElement("div");
                eventsEl.style.display = "none";
                eventsEl.setAttribute("id", eventsId);
                eventsEl.classList.add("calendar__date-events");
                calendar.appendChild(eventsEl);
            }

            link.removeEventListener("click", clickHandler);
            link.addEventListener("click", clickHandler);
        });
        const events = await getMonthEvents(year, month, eventTypes, terms);

        if (0 < events.length) {
            //const datesHeaderInfo = {};
            let prevDateStr = null;
            let prevRow = null;
            events.forEach((event) => {
                const date = moment(event.event_date);
                const year = date.year();
                const month = date.month() + 1;
                const day = date.date();

                if (
                    !Number.isNaN(year) &&
                    !Number.isNaN(month) &&
                    !Number.isNaN(day)
                ) {
                    const yearDisplay = year.toString();
                    const monthDisplay = month.toString().padStart(2, "0");
                    const dayDisplay = day.toString().padStart(2, "0");
                    const dateStr = `${yearDisplay}-${monthDisplay}-${dayDisplay}-${JSON.stringify(
                        terms,
                    )}`;
                    const selector = `
                        table.ui-datepicker-calendar
                        td[data-year="${year}"][data-month="${month - 1}"]
                        [data-date="${day}"]`.trim();
                    const dayElem = datepicker.querySelector(selector);

                    if (dayElem) {
                        const cell = dayElem.closest("td");

                        if (cell) {
                            cell.classList.add("has-event");

                            if (!SHOW_EVENT_DETAILS_IN_MODAL) {
                                let dayEvents = cell.querySelector(
                                    ".calendar__day-events",
                                );

                                if (!dayEvents) {
                                    dayEvents = document.createElement("div");
                                    dayEvents.classList.add(
                                        "calendar__day-events",
                                    );
                                    cell.appendChild(dayEvents);
                                }

                                const eventEl = document.createElement("div");
                                eventEl.classList.add(
                                    "calendar__day-event",
                                    "lc-mb-0",
                                );
                                eventEl.innerHTML = event.content ?? "";

                                const iframe = eventEl.querySelector(
                                    ".calendar__day-event-item-video iframe",
                                );
                                if (iframe) {
                                    const iframeSrc =
                                        iframe.getAttribute("src");
                                    if (iframeSrc) {
                                        const link =
                                            document.createElement("a");
                                        link.setAttribute("href", iframeSrc);
                                        link.setAttribute("data-fancybox", "");
                                        link.textContent =
                                            calendar.getAttribute(
                                                "data-video-label",
                                            );
                                        fancybox ??= getFancybox();
                                        fancybox.bind(link);
                                        iframe.replaceWith(link);
                                    }
                                }

                                dayEvents.appendChild(eventEl);
                            }
                        }
                    }

                    if (!(dateStr in datesFound)) {
                        datesFound[dateStr] = undefined;
                    }

                    if (!SHOW_EVENTS_SIDEBAR) {
                        return;
                    }

                    let row;
                    if (prevDateStr && dateStr === prevDateStr) {
                        row = prevRow;
                    } else {
                        row = document.createElement("tr");
                        eventsTable.appendChild(row);
                    }

                    const link = document.createElement("a");
                    link.setAttribute("href", "#");
                    link.setAttribute("data-date", day);
                    let dateCell;

                    //if ("undefined" === typeof datesHeaderInfo[dateStr]) {
                    if (!prevDateStr || dateStr !== prevDateStr) {
                        dateCell = document.createElement("th");
                        dateCell.classList.add("has-event");
                        dateCell.setAttribute("scope", "row");
                        dateCell.setAttribute("data-year", year);
                        dateCell.setAttribute("data-month", month - 1);
                        const dateCellLink = link.cloneNode(true);
                        dateCellLink.textContent = `${monthDisplay}/${dayDisplay}/${yearDisplay}`;
                        dateCell.appendChild(dateCellLink);
                        //dateCellLink.addEventListener("click", clickHandler);
                        //datesHeaderInfo[dateStr] = {dateCell, rowspan: 0};
                        row.appendChild(dateCell);
                    }
                    /*} else {
                     dateCell = datesHeaderInfo[dateStr].dateCell;
                     }*/

                    let titleCellLink;
                    if (prevDateStr && dateStr === prevDateStr) {
                        titleCellLink = prevRow.querySelector("td a");
                    } else {
                        const titleCell = document.createElement("td");
                        titleCell.classList.add("has-event");
                        titleCell.setAttribute("data-year", year);
                        titleCell.setAttribute("data-month", month - 1);
                        titleCellLink = link.cloneNode(true);
                        titleCellLink.classList.add(
                            "calendar__events-title-cell-link",
                        );
                        titleCell.appendChild(titleCellLink);
                        row.appendChild(titleCell);
                    }

                    const titleCellLinkSpan = document.createElement("span");
                    titleCellLinkSpan.innerHTML = event.title;
                    titleCellLink.appendChild(titleCellLinkSpan);

                    //titleCellLink.addEventListener("click", clickHandler);

                    /*++datesHeaderInfo[dateStr].rowspan;
                     1 < datesHeaderInfo[dateStr].rowspan &&
                     dateCell.setAttribute(
                     "rowspan",
                     "" + datesHeaderInfo[dateStr].rowspan,
                     );*/

                    prevDateStr = dateStr;
                    prevRow = row;
                } else {
                    console.error(`Invalid date: ${event.event_date}`);
                }
            });
        } else {
            if (SHOW_EVENTS_SIDEBAR) {
                noEventsMessage?.classList?.remove("d-none");
            }

            calendar.setAttribute("data-no-events", "true");
        }

        calendar.classList.remove("loading");
    } catch (err) {
        console.error(err);
    }
};

const updateLayoutButton = (button, options, calendar) => {
    const text = options[currentLayout] ?? "";
    const swap = currentLayout;
    currentLayout = currentLayoutToggle;
    currentLayoutToggle = swap;
    calendar.setAttribute("data-layout", currentLayout);

    if (text) {
        button.textContent = text;
    }
};

const validateEventQueryResponseData = (data, view = EVENTS_VIEW_RAW) => {
    view = "string" === typeof view ? view : EVENTS_VIEW_RAW;

    if (!isObject(data)) {
        console.error("Event query response data is not an object", data);
        return false;
    }

    data = convertToPlainObject(data, {
        data: {},
        success: false,
    });

    let defaultEvents;
    switch (view) {
        case EVENTS_VIEW_HTML:
            defaultEvents = "";
            break;
        case EVENTS_VIEW_RAW:
        default:
            defaultEvents = [];
            break;
    }

    const eventsData = convertToPlainObject(data.data, {
        errorMessage: "",
        errorType: "",
        events: defaultEvents,
        invalidFields: null,
        warnings: {},
    });
    eventsData.warnings = convertToPlainObject(eventsData.warnings ?? {}, {
        skippedFields: {},
        skippedTaxonomies: [],
    });
    eventsData.warnings.skippedFields = convertToPlainObject(
        eventsData.warnings.skippedFields ?? {},
    );
    eventsData.warnings.skippedTaxonomies = Array.isArray(
        eventsData.warnings.skippedTaxonomies,
    )
        ? eventsData.warnings.skippedTaxonomies
        : [eventsData.warnings.skippedTaxonomies].filter(Boolean);

    if (!data.success) {
        const errorType = eventsData.errorType;
        const errorTypeStr = errorType ? ` (type ${eventsData.errorType})` : "";
        let errorStr = `Event query responded with an error${errorTypeStr}`;
        eventsData.errorMessage && (errorStr += `: ${eventsData.errorMessage}`);
        console.error(errorStr);

        let invalidFields = null;
        switch (errorType) {
            case "invalidFields":
                eventsData.invalidFields &&
                    console.error(`Invalid fields:`, invalidFields);
                break;
            default:
                break;
        }

        return false;
    }

    switch (view) {
        case EVENTS_VIEW_HTML:
            if ("string" !== typeof eventsData.events) {
                console.error(
                    "Event query response data is invalid (response.data.events is not a string)",
                    eventsData.events,
                );
                return false;
            }

            break;
        case EVENTS_VIEW_RAW:
        default:
            if (!Array.isArray(eventsData.events)) {
                console.error(
                    "Event query response data is invalid (response.data.events is not an array)",
                    eventsData.events,
                );
                return false;
            }

            break;
    }

    if (0 < Object.keys(eventsData.warnings.skippedFields).length) {
        for (let warning of Object.values(eventsData.warnings.skippedFields)) {
            warning = convertToPlainObject(warning, {
                message: "",
            });

            if (!warning.message) {
                continue;
            }

            console.warn(warning.message);
        }
    }

    if (0 < eventsData.warnings.skippedTaxonomies.length) {
        for (let warning of eventsData.warnings.skippedTaxonomies) {
            warning = convertToPlainObject(warning, {
                message: "",
            });

            if (!warning.message) {
                continue;
            }

            console.warn(warning.message);
        }
    }

    return true;
};

export {setupEvents};
